<template>
  <div id="app">
    <header class="app-header">
      <div class="brand">
        <span class="brand-icon">🎬</span>
        <span class="brand-title">{{ platformName }}</span>
      </div>
      <auth-panel :user="user" @auth-change="onAuthChange" @logout="onLogout" @open-page="onOpenPage" />
      <p class="brand-tagline">发现主播精彩 · 长回放智能选段 · 预览下载</p>
    </header>
    <main class="app-main">
      <app-nav
        :current="currentPage"
        :show-segment="featureFixedDurationSegment"
        @change="onNavChange"
      />
      <!-- 工作台用 v-show 保活，避免切到内测说明/定价时中断上传进度与结果回填 -->
      <div v-show="currentPage === 'highlight'">
        <beta-banner ref="betaBanner" />
        <highlight-workspace
          v-if="user && user.emailVerified"
          @quota-refresh="refreshQuotaBanner"
        />
        <el-alert
          v-else-if="user && !user.emailVerified"
          title="请先验证邮箱后再使用精彩分析（验证码见邮件或本地 Java 日志）"
          type="warning"
          :closable="false"
          show-icon
        />
        <el-alert
          v-else
          title="请先登录或注册内测账号"
          type="info"
          :closable="false"
          show-icon
        />
      </div>
      <div v-show="currentPage === 'segment'">
        <el-alert
          v-if="!featureFixedDurationSegment"
          title="固定时长切片暂未开放"
          type="warning"
          :closable="false"
          show-icon
          class="page-alert"
        />
        <template v-else>
          <beta-banner ref="betaBannerSegment" />
          <segment-workspace
            v-if="user && user.emailVerified"
            @quota-refresh="refreshQuotaBanner"
          />
          <el-alert
            v-else-if="user && !user.emailVerified"
            title="请先验证邮箱后再上传与切片（验证码见邮件或本地 Java 日志）"
            type="warning"
            :closable="false"
            show-icon
          />
          <el-alert
            v-else
            title="请先登录或注册内测账号"
            type="info"
            :closable="false"
            show-icon
          />
        </template>
      </div>
      <beta-info-page v-if="currentPage === 'beta'" />
      <pricing-page v-if="currentPage === 'pricing'" />
    </main>
    <footer class="app-footer">
      <span>AIVideoClip · 发现主播精彩</span>
    </footer>
    <feedback-widget v-if="user" />
  </div>
</template>

<script>
import SegmentWorkspace from './components/SegmentWorkspace.vue';
import HighlightWorkspace from './components/HighlightWorkspace.vue';
import AuthPanel from './components/AuthPanel.vue';
import BetaBanner from './components/BetaBanner.vue';
import BetaInfoPage from './components/BetaInfoPage.vue';
import PricingPage from './components/PricingPage.vue';
import AppNav from './components/AppNav.vue';
import FeedbackWidget from './components/FeedbackWidget.vue';
import { PLATFORM_NAME } from './config';
import { clearAuthSession, getStoredUser } from './utils/authStorage';
import { fetchMe } from './api/auth';
import { fetchPublicFeatures } from './api/features';

export default {
  name: 'App',
  components: {
    SegmentWorkspace,
    HighlightWorkspace,
    AuthPanel,
    BetaBanner,
    BetaInfoPage,
    PricingPage,
    AppNav,
    FeedbackWidget,
  },
  data() {
    return {
      platformName: PLATFORM_NAME,
      user: getStoredUser(),
      currentPage: 'highlight',
      featureFixedDurationSegment: false,
    };
  },
  async mounted() {
    await this.loadFeatures();
    if (this.user) {
      try {
        this.user = await fetchMe();
      } catch (e) {
        clearAuthSession();
        this.user = null;
      }
    }
  },
  methods: {
    async loadFeatures() {
      const features = await fetchPublicFeatures();
      this.featureFixedDurationSegment = !!features.featureFixedDurationSegment;
      if (!this.featureFixedDurationSegment && this.currentPage === 'segment') {
        this.currentPage = 'highlight';
      }
    },
    onNavChange(page) {
      if (page === 'segment' && !this.featureFixedDurationSegment) {
        this.$message.warning('固定时长切片暂未开放');
        this.currentPage = 'highlight';
        return;
      }
      this.currentPage = page;
    },
    onAuthChange(user) {
      this.user = user;
      this.$nextTick(() => this.refreshQuotaBanner());
    },
    onOpenPage(page) {
      this.onNavChange(page);
    },
    refreshQuotaBanner() {
      const banner = this.$refs.betaBanner || this.$refs.betaBannerSegment;
      if (banner && banner.refresh) {
        banner.refresh();
      }
    },
    onLogout() {
      clearAuthSession();
      this.user = null;
    },
  },
};
</script>

<style>
* { box-sizing: border-box; }
body {
  margin: 0;
  background: linear-gradient(160deg, #0f172a 0%, #1e293b 45%, #0f766e 100%);
  min-height: 100vh;
}
#app {
  font-family: Avenir, Helvetica, Arial, sans-serif;
  color: #e2e8f0;
  padding: 24px 16px 40px;
}
.app-header {
  max-width: 1100px;
  margin: 0 auto 20px;
}
.brand {
  display: flex;
  align-items: center;
  gap: 10px;
}
.brand-icon { font-size: 28px; }
.brand-title { font-size: 22px; font-weight: 700; flex: 1; }
.brand-tagline {
  margin: 6px 0 0 38px;
  font-size: 14px;
  color: #94a3b8;
}
.app-main {
  max-width: 1100px;
  margin: 0 auto;
}
.page-alert { margin-bottom: 16px; }
.app-footer {
  max-width: 1100px;
  margin: 24px auto 0;
  text-align: center;
  font-size: 12px;
  color: #64748b;
}
</style>
